import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Lightbulb, Clock, Settings, CheckCircle2, Search, MessageSquare, Send, X, Sparkles,
  ShieldCheck, Image as ImageIcon, Star, RotateCcw, Globe, ArrowRight, ThumbsUp, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import { Card } from "./ui";
import { Spinner } from "@/components/common";
import { useI18n } from "@/context/I18nContext";

const STATUS_TONE = {
  vorschlag: { c: "#60a5fa", bg: "rgba(59,130,246,0.14)", bd: "rgba(59,130,246,0.35)" },
  warten_auf_freigabe: { c: "#fbbf24", bg: "rgba(251,191,36,0.14)", bd: "rgba(251,191,36,0.35)" },
  rueckfrage_offen: { c: "#fbbf24", bg: "rgba(251,191,36,0.14)", bd: "rgba(251,191,36,0.35)" },
  geplant: { c: "#c4b5fd", bg: "rgba(139,92,246,0.16)", bd: "rgba(139,92,246,0.38)" },
  in_umsetzung: { c: "#60a5fa", bg: "rgba(59,130,246,0.14)", bd: "rgba(59,130,246,0.35)" },
  erledigt: { c: "#A3E635", bg: "rgba(163,230,53,0.14)", bd: "rgba(163,230,53,0.38)" },
  abgelehnt: { c: "#fca5a5", bg: "rgba(239,68,68,0.14)", bd: "rgba(239,68,68,0.35)" },
  zusatz_vorgeschlagen: { c: "#fbbf24", bg: "rgba(251,191,36,0.14)", bd: "rgba(251,191,36,0.35)" },
};
const EFFORT_DOT = { klein: "#22C55E", mittel: "#F59E0B", gross: "#EF4444" };
const APPROVABLE = ["vorschlag", "warten_auf_freigabe", "rueckfrage_offen", "zusatz_vorgeschlagen"];
const BUCKET = {
  offen: ["vorschlag", "rueckfrage_offen", "zusatz_vorgeschlagen"],
  warten: ["warten_auf_freigabe"],
  umsetzung: ["geplant", "in_umsetzung"],
  erledigt: ["erledigt"],
};
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return ""; } };

const StatusBadge = ({ status, label }) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.vorschlag;
  return <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-bold border" style={{ color: tone.c, background: tone.bg, borderColor: tone.bd }}>{label}</span>;
};

const PriceBadge = ({ idea, t }) => {
  if (idea.price_model === "im_abo") return <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#A3E635]"><CheckCircle2 className="w-3.5 h-3.5" /> {t("Im Abo enthalten")}</span>;
  if (idea.price_model === "zusatzleistung") return <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-300"><ShieldCheck className="w-3.5 h-3.5" /> {idea.price_display}</span>;
  return <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-400"><Clock className="w-3.5 h-3.5" /> {idea.price_display}</span>;
};

const Sel = ({ value, onChange, children, testid }) => (
  <div className="relative">
    <select value={value} onChange={onChange} data-testid={testid}
      className="appearance-none bg-[#15181e] border border-white/10 rounded-xl pl-3.5 pr-9 py-2.5 text-[13px] text-gray-200 focus:outline-none focus:border-[#A3E635] hover:border-white/20 transition-colors cursor-pointer">
      {children}
    </select>
    <svg className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m6 9 6 6 6-6" /></svg>
  </div>
);

export default function CDesign() {
  const { accent, clientId, openAi } = useCockpit();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [action, setAction] = useState(null); // {idea, mode: 'details'|'question'|'reject'}
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [bucket, setBucket] = useState("");
  const [status, setStatus] = useState("");
  const [cat, setCat] = useState("");
  const [price, setPrice] = useState("");
  const [prio, setPrio] = useState("");
  const qs = clientId ? `?client_id=${clientId}` : "";

  const load = () => api.get(`/cockpit/design-ideas${qs}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const term = q.trim().toLowerCase();
    return data.ideas.filter((i) => {
      if (bucket && !(BUCKET[bucket] || []).includes(i.status)) return false;
      if (status && i.status !== status) return false;
      if (cat && i.category !== cat) return false;
      if (price && i.price_model !== price) return false;
      if (prio && i.priority !== prio) return false;
      if (term && !`${i.title} ${i.description} ${i.expected_benefit} ${i.category_label}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [data, q, bucket, status, cat, price, prio]);

  if (!data) return <Spinner />;
  const s = data.stats;
  const cfg = data.config || {};
  const anyFilter = q || bucket || status || cat || price || prio;
  const resetFilters = () => { setQ(""); setBucket(""); setStatus(""); setCat(""); setPrice(""); setPrio(""); };

  const approve = async (id) => {
    setBusy(true);
    try { await api.post(`/cockpit/design-ideas/${id}/approve${qs}`); toast.success(t("Zur Umsetzung freigegeben")); setAction(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); } finally { setBusy(false); }
  };
  const submitAction = async () => {
    if (!action) return;
    if (action.mode === "question" && !text.trim()) { toast.error(t("Bitte formulieren Sie Ihre Frage.")); return; }
    setBusy(true);
    try {
      if (action.mode === "reject") { await api.post(`/cockpit/design-ideas/${action.idea.id}/reject${qs}`, { reason: text }); toast.success(t("Vorschlag abgelehnt")); }
      else { await api.post(`/cockpit/design-ideas/${action.idea.id}/question${qs}`, { question: text }); toast.success(t("Ihre Frage wurde an KEWAG gesendet")); }
      setAction(null); setText(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Aktion fehlgeschlagen")); } finally { setBusy(false); }
  };
  const openMode = (idea, mode) => { setAction({ idea, mode }); setText(""); };

  const KPIS = [
    { key: "offen", icon: Lightbulb, tone: accent, label: t("Offene Vorschläge"), value: s.offene_vorschlaege },
    { key: "warten", icon: Clock, tone: "#F59E0B", label: t("Warten auf Freigabe"), value: s.warten_auf_freigabe },
    { key: "umsetzung", icon: Settings, tone: "#3B82F6", label: t("In Umsetzung"), value: s.in_umsetzung },
    { key: "erledigt", icon: CheckCircle2, tone: "#22C55E", label: t("Erledigt"), value: s.erledigt },
  ];

  const quickActions = [
    { icon: Sparkles, title: t("Verbesserung wünschen"), sub: t("Teilen Sie uns Ihre Idee mit"), onClick: openAi, testid: "dg-qa-wish" },
    { icon: Globe, title: t("Website prüfen lassen"), sub: t("KEWAG prüft Ihre Website"), onClick: openAi, testid: "dg-qa-check" },
    { icon: MessageSquare, title: t("Frage an KEWAG stellen"), sub: t("Wir helfen Ihnen weiter"), onClick: openAi, testid: "dg-qa-ask" },
  ];

  const Aside = (
    <div className="space-y-4" data-testid="dg-aside">
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-2.5"><Lightbulb className="w-4.5 h-4.5" style={{ color: accent }} /><span className="font-bold text-[15px]">{t("Was ist das?")}</span></div>
        <p className="text-[13px] text-gray-400 leading-relaxed">{t("Hier sehen Sie konkrete Vorschläge, wie KEWAG Ihre Website laufend verbessern kann – für mehr Vertrauen, bessere Sichtbarkeit und mehr Anfragen. Sie können Vorschläge prüfen, kommentieren oder freigeben.")}</p>
      </Card>

      <Card className="p-5">
        <div className="font-bold text-[15px] mb-3">{t("Schnellaktionen")}</div>
        <div className="space-y-1">
          {quickActions.map((a) => (
            <button key={a.testid} onClick={a.onClick} data-testid={a.testid}
              className="w-full flex items-center gap-3 rounded-xl px-2.5 py-2.5 hover:bg-white/[0.05] transition-colors text-left group">
              <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A`, color: accent }}><a.icon className="w-4.5 h-4.5" /></span>
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-gray-100 truncate">{a.title}</span>
                <span className="block text-[11px] text-gray-500 truncate">{a.sub}</span>
              </span>
              <ArrowRight className="w-4 h-4 text-gray-600 ml-auto shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </button>
          ))}
        </div>
      </Card>

      {data.categories.length > 0 && (
        <Card className="p-5" data-testid="dg-categories">
          <div className="font-bold text-[15px] mb-3">{t("Kategorien")}</div>
          <div className="space-y-0.5">
            {data.categories.map((c) => (
              <button key={c.key} onClick={() => { setCat(cat === c.key ? "" : c.key); setBucket(""); }} data-testid={`dg-cat-${c.key}`}
                className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 transition-colors text-left ${cat === c.key ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"}`}>
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent }} />
                <span className="text-[13px] text-gray-300 flex-1 truncate">{t(c.label)}</span>
                <span className="text-[11px] font-bold text-gray-400 bg-white/5 border border-white/10 rounded-full px-2 py-0.5 shrink-0">{c.count}</span>
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5" style={{ borderColor: `${accent}44`, background: `${accent}0A` }} data-testid="dg-tip">
        <div className="flex items-center gap-2 mb-2"><Star className="w-4.5 h-4.5" style={{ color: accent }} /><span className="font-bold text-[15px]">{t("Tipp")}</span></div>
        <p className="text-[13px] text-gray-300 leading-relaxed">{t("Sie können uns jederzeit ein Foto, einen Screenshot oder einen Wunsch senden. KEWAGpage macht daraus einen sauberen nächsten Schritt.")}</p>
        <button onClick={openAi} data-testid="dg-tip-send" className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-bold" style={{ color: accent }}>
          {t("Jetzt senden")} <ArrowRight className="w-4 h-4" />
        </button>
      </Card>
    </div>
  );

  return (
    <div className="fade-in" data-testid="cockpit-design">
      <div className="mb-5">
        <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight">{t("Website verbessern")}</h1>
        <p className="text-sm text-gray-400 mt-1.5 max-w-2xl">{t("Konkrete Vorschläge von KEWAG für mehr Vertrauen, bessere Sichtbarkeit und mehr Anfragen.")}</p>
      </div>

      {/* KPI-Karten */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {KPIS.map((k) => {
          const activeK = bucket === k.key;
          return (
            <button key={k.key} onClick={() => { setBucket(activeK ? "" : k.key); setStatus(""); }} data-testid={`dg-kpi-${k.key}`}
              className={`text-left rounded-2xl p-4 lg:p-5 border transition-all hover:-translate-y-0.5 ${activeK ? "bg-[#171b14]" : "bg-[#15181e] border-white/5 hover:border-white/15"}`}
              style={activeK ? { borderColor: `${k.tone}66`, boxShadow: `inset 0 0 0 1px ${k.tone}22` } : {}}>
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${k.tone}1F`, color: k.tone }}><k.icon className="w-4.5 h-4.5" /></span>
                <span className="text-[13px] font-semibold text-gray-300 leading-tight">{k.label}</span>
              </div>
              <div className="text-[28px] lg:text-[32px] font-black tracking-tight mt-2.5">{k.value}</div>
              <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: k.tone }}>{t("Alle ansehen")} <ArrowRight className="w-3 h-3" /></div>
            </button>
          );
        })}
      </div>

      <div className="lg:flex lg:gap-6 lg:items-start">
        {/* Hauptspalte */}
        <div className="flex-1 min-w-0">
          {/* Filter */}
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="dg-filters">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="dg-search" placeholder={t("Vorschläge durchsuchen…")}
                className="w-full bg-[#15181e] border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-[13px] text-gray-200 focus:outline-none focus:border-[#A3E635] hover:border-white/20 transition-colors" />
            </div>
            <Sel value={status} onChange={(e) => { setStatus(e.target.value); setBucket(""); }} testid="dg-filter-status">
              <option value="">{t("Status: Alle")}</option>
              {Object.entries(cfg.status_labels || {}).map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
            </Sel>
            <Sel value={cat} onChange={(e) => setCat(e.target.value)} testid="dg-filter-cat">
              <option value="">{t("Kategorie: Alle")}</option>
              {Object.entries(cfg.category_labels || {}).map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
            </Sel>
            <Sel value={price} onChange={(e) => setPrice(e.target.value)} testid="dg-filter-price">
              <option value="">{t("Inklusive / Zusatzleistung: Alle")}</option>
              <option value="im_abo">{t("Im Abo enthalten")}</option>
              <option value="zusatzleistung">{t("Zusatzleistung")}</option>
            </Sel>
            <Sel value={prio} onChange={(e) => setPrio(e.target.value)} testid="dg-filter-prio">
              <option value="">{t("Priorität: Alle")}</option>
              <option value="hoch">{t("Hoch")}</option>
              <option value="normal">{t("Normal")}</option>
              <option value="tief">{t("Tief")}</option>
            </Sel>
            <button onClick={resetFilters} disabled={!anyFilter} data-testid="dg-filter-reset"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-400 hover:text-white px-3 py-2.5 rounded-xl border border-white/10 hover:bg-white/5 transition-colors disabled:opacity-40 disabled:cursor-default">
              <RotateCcw className="w-3.5 h-3.5" /> {t("Filter zurücksetzen")}
            </button>
          </div>

          {/* Liste */}
          {data.ideas.length === 0 ? (
            <EmptyState accent={accent} t={t} openAi={openAi} />
          ) : filtered.length === 0 ? (
            <Card className="p-8 text-center" data-testid="dg-no-match">
              <p className="text-sm text-gray-400">{t("Keine Vorschläge für diese Filter.")}</p>
              <button onClick={resetFilters} className="mt-3 text-[13px] font-bold" style={{ color: accent }}>{t("Filter zurücksetzen")}</button>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="dg-list">
              {filtered.map((i) => (
                <IdeaCard key={i.id} idea={i} accent={accent} t={t} busy={busy}
                  onDetails={() => openMode(i, "details")} onQuestion={() => openMode(i, "question")}
                  onApprove={() => approve(i.id)} />
              ))}
            </div>
          )}
        </div>

        {/* Seitenleiste (Desktop rechts, Mobile unten als Cards) */}
        <aside className="lg:w-[330px] lg:shrink-0 mt-6 lg:mt-0">{Aside}</aside>
      </div>

      {/* Modal: Details / Frage / Ablehnen */}
      {action && (
        <div className="fixed inset-0 z-[9998] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4" data-testid="dg-modal" onClick={() => setAction(null)}>
          <div className="bg-[#121217] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#121217] z-10">
              <h3 className="font-bold text-[15px] truncate pr-3">{action.mode === "details" ? action.idea.title : action.mode === "reject" ? t("Vorschlag ablehnen") : t("Frage an KEWAG stellen")}</h3>
              <button onClick={() => setAction(null)} data-testid="dg-modal-close" className="text-gray-400 hover:text-white shrink-0"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {action.mode === "details" ? (
                <DetailsBody idea={action.idea} accent={accent} t={t} busy={busy}
                  onApprove={() => approve(action.idea.id)} onQuestion={() => setAction({ ...action, mode: "question" })} onReject={() => setAction({ ...action, mode: "reject" })} />
              ) : (
                <div>
                  <p className="text-[13px] text-gray-400 mb-3">{action.mode === "reject" ? t("Möchten Sie diesen Vorschlag ablehnen? Sie können optional einen Grund angeben.") : t("Stellen Sie Ihre Frage zu diesem Vorschlag – KEWAG meldet sich bei Ihnen.")}</p>
                  <div className="rounded-xl bg-white/[0.03] border border-white/10 px-3.5 py-2.5 mb-3 text-[13px] font-semibold text-gray-200">{action.idea.title}</div>
                  <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="dg-modal-input"
                    placeholder={action.mode === "reject" ? t("Grund (optional)") : t("Ihre Frage …")}
                    className="w-full bg-black border border-white/10 rounded-xl px-3.5 py-3 text-sm focus:outline-none focus:border-[#A3E635]" />
                  <div className="flex items-center gap-2 mt-4">
                    <button onClick={submitAction} disabled={busy} data-testid="dg-modal-submit"
                      className="text-black font-bold rounded-xl px-5 py-2.5 flex items-center gap-2 disabled:opacity-60" style={{ background: accent }}>
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {action.mode === "reject" ? t("Ablehnen") : t("Frage senden")}
                    </button>
                    <button onClick={() => setAction(null)} className="text-sm text-gray-400 hover:text-white px-3 py-2.5">{t("Abbrechen")}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function IdeaCard({ idea: i, accent, t, busy, onDetails, onQuestion, onApprove }) {
  const canApprove = i.needs_client_approval && APPROVABLE.includes(i.status);
  const inProgress = ["geplant", "in_umsetzung"].includes(i.status);
  return (
    <Card className="p-0 overflow-hidden" data-testid={`dg-idea-${i.id}`}>
      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-[190px] shrink-0 bg-white/[0.03] border-b lg:border-b-0 lg:border-r border-white/10">
          <div className="aspect-[16/10] lg:h-full lg:aspect-auto lg:min-h-[150px] flex items-center justify-center overflow-hidden">
            {i.preview_image_url ? <img src={i.preview_image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-7 h-7 text-gray-700" />}
          </div>
        </div>

        <div className="flex-1 min-w-0 p-4 lg:p-5">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-[15px] lg:text-base" style={{ color: accent }}>{i.title}</h3>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300">{t(i.category_label)}</span>
          </div>
          {i.description && <p className="text-[13px] text-gray-400 mt-1.5 line-clamp-2">{i.description}</p>}
          {i.expected_benefit && <p className="text-[12px] text-gray-300 mt-1.5"><span className="text-gray-500">{t("Nutzen")}: </span>{i.expected_benefit}</p>}
          <div className="flex items-center gap-4 flex-wrap mt-3">
            {i.effort_label && <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-300"><span className="w-2 h-2 rounded-full" style={{ background: EFFORT_DOT[i.effort] || "#9ca3af" }} /> {t("Aufwand")}: {t(i.effort_label)}</span>}
            <PriceBadge idea={i} t={t} />
          </div>
        </div>

        <div className="lg:w-[180px] shrink-0 p-4 lg:p-5 border-t lg:border-t-0 lg:border-l border-white/10 flex flex-col gap-2">
          <StatusBadge status={i.status} label={t(i.status_label)} />
          {i.next_step && <div className="mt-1"><div className="text-[10px] uppercase tracking-wider text-gray-500">{t("Nächster Schritt")}</div><div className="text-[12px] text-gray-300 mt-0.5">{i.next_step}</div></div>}
          <div className="text-[11px] text-gray-500 mt-auto pt-1">{t("Aktualisiert")}: {fmtDate(i.updated_at)}</div>
        </div>

        <div className="lg:w-[190px] shrink-0 p-4 lg:p-5 border-t lg:border-t-0 lg:border-l border-white/10 flex flex-row lg:flex-col gap-2 justify-center flex-wrap">
          <button onClick={onDetails} data-testid={`dg-details-${i.id}`}
            className="flex-1 lg:flex-none bg-white/5 text-white text-[13px] font-semibold rounded-xl px-3 py-2.5 border border-white/10 hover:bg-white/10 transition-colors">{t("Details ansehen")}</button>
          <button onClick={onQuestion} data-testid={`dg-question-${i.id}`}
            className="flex-1 lg:flex-none bg-white/5 text-white text-[13px] font-semibold rounded-xl px-3 py-2.5 border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> {t("Frage stellen")}</button>
          {canApprove ? (
            <button onClick={onApprove} disabled={busy} data-testid={`dg-approve-${i.id}`}
              className="flex-1 lg:flex-none text-black text-[13px] font-bold rounded-xl px-3 py-2.5 flex items-center justify-center gap-1.5 disabled:opacity-60" style={{ background: accent }}><ThumbsUp className="w-3.5 h-3.5" /> {t("Freigeben")}</button>
          ) : inProgress ? (
            <button disabled data-testid={`dg-inprogress-${i.id}`}
              className="flex-1 lg:flex-none bg-white/[0.03] text-gray-500 text-[13px] font-semibold rounded-xl px-3 py-2.5 border border-white/10 flex items-center justify-center gap-1.5 cursor-default"><ThumbsUp className="w-3.5 h-3.5" /> {t("In Umsetzung")}</button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function DetailsBody({ idea: i, accent, t, busy, onApprove, onQuestion, onReject }) {
  const canApprove = i.needs_client_approval && APPROVABLE.includes(i.status);
  return (
    <div>
      {i.preview_image_url && <div className="rounded-xl overflow-hidden border border-white/10 mb-4"><img src={i.preview_image_url} alt="" className="w-full max-h-56 object-cover" /></div>}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <StatusBadge status={i.status} label={t(i.status_label)} />
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300">{t(i.category_label)}</span>
      </div>
      {i.description && <p className="text-[13px] text-gray-300 leading-relaxed">{i.description}</p>}
      <div className="grid grid-cols-2 gap-3 mt-4">
        {i.expected_benefit && <Field label={t("Nutzen")} value={i.expected_benefit} />}
        {i.next_step && <Field label={t("Nächster Schritt")} value={i.next_step} />}
        {i.effort_label && <Field label={t("Aufwand")} value={t(i.effort_label)} />}
        <Field label={t("Preislogik")} value={i.price_display} />
        <Field label={t("Aktualisiert")} value={fmtDate(i.updated_at)} />
      </div>
      {i.client_comment && <div className="mt-3 rounded-xl bg-sky-400/10 border border-sky-400/20 px-3.5 py-2.5 text-[12px] text-sky-200"><MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />{t("Ihre Rückfrage")}: {i.client_comment}</div>}
      {i.rejection_reason && <div className="mt-2 rounded-xl bg-red-400/10 border border-red-400/20 px-3.5 py-2.5 text-[12px] text-red-200">{t("Abgelehnt")}: {i.rejection_reason}</div>}
      <div className="flex items-center gap-2 mt-5 flex-wrap">
        {canApprove && <button onClick={onApprove} disabled={busy} data-testid="dg-details-approve" className="text-black font-bold rounded-xl px-4 py-2.5 flex items-center gap-2 disabled:opacity-60" style={{ background: accent }}><ThumbsUp className="w-4 h-4" /> {t("Freigeben")}</button>}
        <button onClick={onQuestion} data-testid="dg-details-question" className="bg-white/5 text-white font-semibold rounded-xl px-4 py-2.5 border border-white/10 hover:bg-white/10 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> {t("Frage stellen")}</button>
        {canApprove && <button onClick={onReject} data-testid="dg-details-reject" className="bg-white/5 text-white font-semibold rounded-xl px-4 py-2.5 border border-white/10 hover:bg-white/10 flex items-center gap-2"><X className="w-4 h-4" /> {t("Ablehnen")}</button>}
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
    <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
    <div className="text-[13px] text-gray-200 mt-0.5">{value}</div>
  </div>
);

function EmptyState({ accent, t, openAi }) {
  return (
    <Card className="p-8 lg:p-10 text-center" data-testid="dg-empty">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${accent}1A` }}><Lightbulb className="w-7 h-7" style={{ color: accent }} /></div>
      <h3 className="font-bold text-lg">{t("Noch keine aktiven Verbesserungsvorschläge")}</h3>
      <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">{t("KEWAG prüft Ihre Website laufend. Sobald ein konkreter Vorschlag für mehr Vertrauen, bessere Sichtbarkeit oder mehr Anfragen vorbereitet ist, erscheint er hier.")}</p>
      <div className="flex items-center justify-center gap-2 flex-wrap mt-5">
        <button onClick={openAi} data-testid="dg-empty-wish" className="text-black font-bold rounded-xl px-4 py-2.5 flex items-center gap-2" style={{ background: accent }}><Sparkles className="w-4 h-4" /> {t("Verbesserung wünschen")}</button>
        <button onClick={openAi} data-testid="dg-empty-check" className="bg-white/5 text-white font-semibold rounded-xl px-4 py-2.5 border border-white/10 hover:bg-white/10 flex items-center gap-2"><Globe className="w-4 h-4" /> {t("Website prüfen lassen")}</button>
        <button onClick={openAi} data-testid="dg-empty-ask" className="bg-white/5 text-white font-semibold rounded-xl px-4 py-2.5 border border-white/10 hover:bg-white/10 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> {t("Frage an KEWAG stellen")}</button>
      </div>
      <p className="text-[12px] text-gray-500 mt-5 max-w-md mx-auto">{t("Sie können uns jederzeit ein Foto, einen Screenshot oder einen Wunsch senden. KEWAGpage macht daraus einen sauberen nächsten Schritt.")}</p>
    </Card>
  );
}
